import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { graphql, useQueryLoader, type PreloadedQuery } from 'react-relay';

import {
    getUserGeoInfo,
    WAIT_FOR_SHIPPING_REGIONS,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useUserShippingRegionsUrlLabels } from '../../PdpSharedLowerShippingInfo/hooks/useUserShippingRegionsUrlLabels';
import { updateRelayVariables } from '../../../actions/sharedRelayVariableActions';
import { getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2';

import {
    type usePdpShippingInfoQueryQuery as usePdpShippingInfoQueryQueryType,
    type usePdpShippingInfoQueryQuery$variables,
} from './__generated__/usePdpShippingInfoQueryQuery.graphql';

//In this case we won't satisfy the eslint rule since the child component is using theses fields
/* eslint-disable relay/unused-fields */
export const usePdpShippingInfoQueryQuery = graphql`
    query usePdpShippingInfoQueryQuery(
        $itemId: String!
        $userId: String!
        $fetchRegionalInfo: Boolean = false
        $userZipCode: String = ""
        $userCountryCode: String = ""
        $isTrade: Boolean = false
        $fetchItem: Boolean = false
        $fetchUser: Boolean = false
        $afterDisplay: Boolean = false
        $itemQuantity: Int
        $shippingRegions: [String] = []
        $skuId: String
        $isDutiesEstimatesVariant: Boolean = false
    ) {
        viewer {
            ...PdpSharedShippingInfoLink_viewer
            ...PdpSharedPromoCode_viewer
            ...PdpSharedShippingAddress_viewer
            ...PdpSharedDutiesMessage_viewer
            ...PdpSharedEstimatedDutiesMessage_viewer
            ...PdpSharedLowerShippingInfo_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
            ...useShippingType_viewer
                @arguments(
                    userId: $userId
                    itemId: $itemId
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                    itemQuantity: $itemQuantity
                )
            regionSelectorEnabled: featureFlag(feature: "pdpRegionSelector")
            geography {
                countries {
                    name
                    abbreviation
                }
            }
            relevantShipping(
                itemId: $itemId
                buyerId: $userId
                quantity: $itemQuantity
                zipCode: $userZipCode
                countryCode: $userCountryCode
            ) {
                quoteRequestStatus
                mlFreightQuotesAbTestVariant
                shipmentQuote {
                    ...PdpSharedShippingInfoLink_shipmentQuote
                    ...TrackQuoteShown_shipmentQuote
                    type
                    handlingTimeDisplay(displayShort: true)
                    subTotalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    buyerServiceMethodDisplay
                    showQuoteAsPending
                    serviceMethod {
                        serviceType
                        serviceMethodType
                    }
                }
                shipment {
                    shippingAddress {
                        displayCityStateCountry
                    }
                }
            }
            itemRead(itemIds: [$itemId]) @include(if: $fetchItem) {
                ... on Item {
                    ...PdpSharedLowerShippingInfo_item
                        @arguments(
                            isTrade: $isTrade
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userCountryCode: $userCountryCode
                            userZipCode: $userZipCode
                            shippingRegions: $shippingRegions
                        )
                    ...useShippingPrice_item
                        @arguments(
                            buyerId: $userId
                            fetchShippingCostInfo: true
                            itemQuantity: $itemQuantity
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                        )

                    ...useShippingType_item
                        @arguments(
                            userId: $userId
                            itemQuantity: $itemQuantity
                            userZipCode: $userZipCode
                            userCountryCode: $userCountryCode
                        )
                    ...PdpSharedShippingInfoLink_item
                    ...PdpSharedShippingInfoWrapper_item
                    ...PdpSharedDutiesMessage_item
                    serviceId
                    isItemRTQuoteEligibly(
                        buyerId: $userId
                        countryCode: $userCountryCode
                        zipCode: $userZipCode
                    )
                    isDutiesEstimateEligible(
                        buyerId: $userId
                        shippingAddress: { country: $userCountryCode, zipCode: $userZipCode }
                    )
                    vertical
                    address {
                        displayCountry
                        country
                    }
                    ...PdpSharedShippingAndReturns_item
                    ...PdpSharedReturns_item
                    ...useShowReturns_item
                }
            }
            user(userId: $userId) @include(if: $fetchUser) {
                ...PdpSharedPromoCode_user
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingAddress_user
                ...useUserGeoInfo_user
                ...PdpSharedReturns_user
                ...useShippingAndReturns_user
                serviceId
                addressBook {
                    proAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    personalAddress {
                        displayCountry
                        country
                        zipCode
                    }
                    addressList {
                        displayCountry
                        country
                        zipCode
                    }
                }
            }
        }
    }
`;
/* eslint-disable relay/unused-fields */

type UsePdpShippingInfoQueryParams = {
    itemId?: string | null;
    userId?: string | null;
    isTradeUser?: boolean | null;
    isClient?: boolean;
    itemQuantity?: number | null;
    skuId: string | null;
};

function isDutiesEstimatesVariant(): boolean {
    return getAbTestV2('dutiesEstimatesV2')?.variant === 'variant';
}

export const usePdpShippingInfoQuery = ({
    itemId,
    userId,
    isTradeUser,
    isClient = false,
    itemQuantity,
    skuId,
}: UsePdpShippingInfoQueryParams): {
    queryReference: PreloadedQuery<usePdpShippingInfoQueryQueryType> | null | undefined;
    reloadQuery: (variables: Partial<usePdpShippingInfoQueryQuery$variables>) => void;
    showSpinner: boolean;
    isLoading: boolean;
} => {
    const [queryReference, loadQuery] = useQueryLoader<usePdpShippingInfoQueryQueryType>(
        usePdpShippingInfoQueryQuery
    );
    const dispatch = useDispatch();
    const shippingRegions = useUserShippingRegionsUrlLabels();
    const [showSpinner, setShowSpinner] = useState(true);
    const hasLoadedQueryRef = useRef(false);
    const reloadQuery = useCallback(
        (variables: Partial<usePdpShippingInfoQueryQuery$variables>) => {
            /**
             * Wait for shipping regions to be populated (either an array or undefined)
             * By doing this we avoid unnecessary shipmentQuotes/live calls
             */
            if (shippingRegions !== WAIT_FOR_SHIPPING_REGIONS) {
                const userGeoInfo = getUserGeoInfo();
                if (typeof window !== 'undefined') {
                    (window as $TSFixMe).__DIBS_QUOTE_TIMING__ = Date.now();
                }

                loadQuery(
                    {
                        itemId: itemId || '',
                        userId: userId || '',
                        isTrade: isTradeUser,
                        fetchUser: !!userId,
                        fetchItem: !!itemId,
                        afterDisplay: isClient,
                        skuId,
                        isDutiesEstimatesVariant: isDutiesEstimatesVariant(),
                        itemQuantity,
                        userCountryCode: userGeoInfo?.countryCode || '',
                        userZipCode: userGeoInfo?.zipCode || '',
                        shippingRegions,
                        // We want to override query variables with the new ones when refetching the query
                        ...variables,
                    },
                    { fetchPolicy: 'network-only' }
                );
                dispatch(
                    updateRelayVariables({
                        updateItemPricing: true,
                        updatedUserCountryCode: variables?.userCountryCode,
                    })
                );
            }
        },
        [
            loadQuery,
            dispatch,
            itemId,
            userId,
            isTradeUser,
            isClient,
            itemQuantity,
            shippingRegions,
            skuId,
        ]
    );

    useEffect(() => {
        /**
         * Wait for shipping regions to be populated (either an array or undefined)
         * By doing this we avoid unnecessary shipmentQuotes/live calls
         */
        if (
            isClient &&
            shippingRegions !== WAIT_FOR_SHIPPING_REGIONS &&
            !hasLoadedQueryRef.current
        ) {
            const userGeoInfo = getUserGeoInfo();
            if (typeof window !== 'undefined') {
                (window as $TSFixMe).__DIBS_QUOTE_TIMING__ = Date.now();
            }

            loadQuery(
                {
                    itemId: itemId || '',
                    userId: userId || '',
                    isTrade: isTradeUser,
                    fetchUser: !!userId,
                    fetchItem: !!itemId,
                    afterDisplay: isClient,
                    userCountryCode: userGeoInfo?.countryCode || '',
                    userZipCode: userGeoInfo?.zipCode || '',
                    itemQuantity,
                    skuId,
                    isDutiesEstimatesVariant: isDutiesEstimatesVariant(),
                    shippingRegions,
                    fetchRegionalInfo: false,
                },
                { fetchPolicy: 'network-only' }
            );
            hasLoadedQueryRef.current = true;
        }
    }, [loadQuery, isClient, itemId, userId, isTradeUser, itemQuantity, shippingRegions, skuId]);

    useEffect(() => {
        if (hasLoadedQueryRef.current) {
            reloadQuery({ itemQuantity, skuId });
        }
    }, [itemQuantity, reloadQuery, skuId]);

    useEffect(() => {
        if (showSpinner) {
            const timer = setTimeout(() => setShowSpinner(false), 2000);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [showSpinner]);

    return {
        queryReference,
        reloadQuery,
        showSpinner,
        isLoading: !queryReference || showSpinner,
    };
};
